import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Theme, VerticalSpace } from '@arc-ui/components';
import { AEMFooterV2 } from '@bt/bt-business-shared-footer';
import { MegaNavigationHeader } from '@bt/frontend-framework-navigation-header';
import { authService } from 'auth/services';
import classNames from 'classnames';

import { fetchOrderTrackerRequests } from 'features/order-tracker/store/actions';
import { useAttachScript, useLivePersonChatActivityObserver } from 'features/order-tracker/store/hooks';
import { useAnalyticsTags } from 'features/order-tracker/store/hooks/useAnalyticsTags';
import { useSetUserStatus } from 'features/order-tracker/store/hooks/useSetUserStatus';
import { pageLoadAnalyticsService } from 'features/order-tracker/store/sagas/services/pageLoadAnalyticsService';

import { ACCOUNT_URL, LOG_OUT, LOG_OUT_URL, LOGIN_REGISTRATION } from './constants';

import '@arc-ui/themes/lib/bt-business.css';
import styles from './styles.module.scss';

interface PageTemplateProps {
  children: React.ReactNode;
}

export function PageTemplate({ children }: PageTemplateProps): React.ReactElement | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState<boolean>(false);
  const isAuthenticated: boolean = authService.isAuthenticated();
  useSetUserStatus(isAuthenticated);

  const dispatch = useDispatch();
  const analyticsTags = useAnalyticsTags();
  const REACT_APP_AEM_HEADLESS_HEADER_BASE: string = import.meta.env.VITE_AEM_HEADLESS_HEADER_BASE;
  const REACT_APP_MY_ACCOUNT_DOMAIN: string = import.meta.env.VITE_MY_ACCOUNT_DOMAIN;
  const REACT_APP_AEM_HEADLESS_FOOTER_V2_BASE: string = import.meta.env.VITE_AEM_HEADLESS_FOOTER_V2_BASE;

  const authBtnTitle: string = isAuthenticated ? LOG_OUT : LOGIN_REGISTRATION;
  const authBtnHref: string = isAuthenticated
    ? `${REACT_APP_MY_ACCOUNT_DOMAIN}${LOG_OUT_URL}`
    : `${REACT_APP_MY_ACCOUNT_DOMAIN}${ACCOUNT_URL}`;

  useEffect(() => {
    (window as unknown as Record<string, unknown>).recaptchaOnloadCallback = () => {
      setRecaptchaLoaded(true);
    };
    isAuthenticated && authService.throttledRefreshToken();
  }, []);

  useEffect(() => {
    if (recaptchaLoaded) {
      pageLoadAnalyticsService.setComponentRef(containerRef);
      dispatch(fetchOrderTrackerRequests());
    }
  }, [recaptchaLoaded]);

  useAttachScript(import.meta.env.VITE_ADOBE_LAUNCH_SCRIPT_URL, true, false);
  useAttachScript(`${import.meta.env.VITE_GOOGLE_RECAPTCHA_URL}${import.meta.env.VITE_RECAPTCHA_SITE_KEY}`, true, true);
  useLivePersonChatActivityObserver();

  if (!recaptchaLoaded) {
    return null;
  }

  return (
    <div ref={containerRef} className={classNames(styles.orderTracker, 'component-order-tracker')} {...analyticsTags}>
      <Theme theme="bt-business">
        <MegaNavigationHeader
          baseUrl={REACT_APP_AEM_HEADLESS_HEADER_BASE}
          loginData={{
            title: authBtnTitle,
            url: authBtnHref,
            onLoginClick: isAuthenticated ? authService.handleLogout : undefined,
          }}
        />
        {/* For spacing */}
        <VerticalSpace size="16" />
      </Theme>

      {children}
      <Theme theme="bt-business">
        <AEMFooterV2
          ssr={false}
          baseUrl={REACT_APP_AEM_HEADLESS_HEADER_BASE}
          jsonUrl={REACT_APP_AEM_HEADLESS_FOOTER_V2_BASE}
        />
      </Theme>
    </div>
  );
}
